import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Box,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";

const SCOPES = [
  {
    id: "p3_prompts",
    label: "Organization prompt settings",
    description:
      "ASI may review anonymized patterns in your organization-specific prompt customizations to improve platform defaults.",
  },
  {
    id: "usage_metrics",
    label: "Usage metrics",
    description:
      "ASI may analyze aggregated LLM usage statistics for your organization to improve reliability and cost controls.",
  },
  {
    id: "session_data",
    label: "Session metadata",
    description:
      "ASI may analyze non-clinical session metadata (counts, durations, outcomes) to improve product quality.",
  },
];

const DataSharingSettingsPage = () => {
  const { userData, organization, organizationId } = useAuth();
  const [consent, setConsent] = useState(null);
  const [loadingScope, setLoadingScope] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setConsent(organization?.asiDataSharingConsent || { granted: false, scope: [] });
  }, [organization]);

  if (userData?.role !== "admin") {
    return <Navigate to="/dashboard/settings" replace />;
  }

  const activeScopes = new Set(Array.isArray(consent?.scope) ? consent.scope : []);

  const handleToggle = async (scopeId, nextGranted) => {
    setLoadingScope(scopeId);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const callable = firebase
        .app()
        .functions("us-central1")
        .httpsCallable("updateAsiDataSharingConsent");

      await callable({
        organizationId,
        scope: scopeId,
        granted: nextGranted,
      });

      setConsent((prev) => {
        const scopes = new Set(Array.isArray(prev?.scope) ? prev.scope : []);
        if (nextGranted) scopes.add(scopeId);
        else scopes.delete(scopeId);
        return {
          ...prev,
          granted: scopes.size > 0,
          scope: Array.from(scopes),
        };
      });
      setSuccessMessage(
        nextGranted ? "Consent granted for this scope." : "Consent revoked for this scope.",
      );
    } catch (err) {
      setErrorMessage(err.message || "Unable to update consent.");
    } finally {
      setLoadingScope(null);
    }
  };

  return (
    <Box>
      <DashboardPageHeader
        title="Data sharing with ASI"
        subtitle="Control whether Applied SuperIntelligence may use your organization's data for platform improvement. Support access for specific incidents is managed separately."
      />

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Stack spacing={2}>
        {SCOPES.map((scope) => (
          <Paper key={scope.id} sx={{ p: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={activeScopes.has(scope.id)}
                  onChange={(event) => handleToggle(scope.id, event.target.checked)}
                  disabled={loadingScope === scope.id}
                />
              }
              label={scope.label}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {scope.description}
            </Typography>
            {loadingScope === scope.id && (
              <LoadingButton loading sx={{ mt: 1 }} />
            )}
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default DataSharingSettingsPage;
